import { HgPageNotFound, HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { defineComponent, h } from 'vue';
import {
  createMemoryHistory,
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type Router,
  type RouteRecordRaw,
} from 'vue-router';

import useFeatureToggle, { FeatureToggles } from '@/composables/useFeatureToggle';
import { normalizeAgencyAgentsFeatureFlagQuery } from '@/helpers/Directory/featureToggleQueryParams';
import { isServer } from '@/helpers/Platform/platform';
import { getAgentHomeRouteName, getHomeRouteName } from '@/helpers/Url/Url';
import { setLocaleBeforeRouteChange } from '@/i18n-setup';
import AgencyDirectoryPage from '@/pages/AgencyDirectoryPage/AgencyDirectoryPage.vue';
import AgencySRP from '@/pages/AgencySRP/AgencyDirectoryPage.vue';
import AgentDirectoryPage from '@/pages/AgentDirectoryPage/AgentDirectoryPage.vue';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';

const VIEW_NAME_PREFIX = 'Agency Directory';
const AGENT_VIEW_NAME_PREFIX = 'Agent Directory';

const AgencyDirectoryRouteComponent = defineComponent({
  name: 'AgencyDirectoryRoute',
  setup() {
    const isAgencyAgentsOn = useFeatureToggle(FeatureToggles.AGENCY_AGENTS);

    return () => h(isAgencyAgentsOn.value ? AgencySRP : AgencyDirectoryPage);
  },
});

export default function createAgencyRouter(defaultLanguage: string): Router {
  const router = createRouter({
    history: isServer()
      ? createMemoryHistory(import.meta.env.VUE_BASE_PATH)
      : createWebHistory(import.meta.env.VUE_BASE_PATH),
    routes: [
      ...generateHomePageRoutes(),
      ...generateAgentHomePageRoutes(),
      {
        path: '/.*',
        name: 'pageNotFound',
        component: () => HgPageNotFound,
        props: () => ({
          pageLayoutCssClass: 'layout-container',
        }),
        meta: {
          locale: HgSupportedLanguage.EN,
          getLanguageSwitchLink(route: RouteLocationNormalized) {
            return {
              path: route.path,
            };
          },
        },
      } as unknown as RouteRecordRaw,
    ],
  });

  router.beforeEach(
    async (targetRoute: RouteLocationNormalized, _sourceRoute: RouteLocationNormalized, next) => {
      const normalizedFeatureFlagQuery = normalizeAgencyAgentsFeatureFlagQuery(targetRoute.query);

      if (normalizedFeatureFlagQuery) {
        next({
          ...targetRoute,
          query: normalizedFeatureFlagQuery,
          replace: true,
        });
        return;
      }

      const locale = (targetRoute.params.locale ||
        targetRoute?.meta?.locale ||
        defaultLanguage) as unknown as string;

      await setLocaleBeforeRouteChange(locale as HgSupportedLanguage);

      next();
    },
  );

  setupRumViewTracking(router);

  return router;
}

const localizedHomepagePaths = {
  de: '/agenturverzeichnis/:location?',
  fr: '/liste-agences/:location?',
  it: '/elenco-agenzie/:location?',
  en: '/agency-directory/:location?',
};

const localizedAgentHomepagePaths = {
  de: '/maklerverzeichnis/:location?',
  fr: '/annuaire-courtiers/:location?',
  it: '/elenco-agenti/:location?',
  en: '/agent-directory/:location?',
};

export const getBasePathWithLocation = (language: HgSupportedLanguage, location?: string): string =>
  localizedHomepagePaths[language].replace(/\/:location\??/g, location ? `/${location}` : '');

export const getAgentBasePathWithLocation = (
  language: HgSupportedLanguage,
  location?: string,
): string =>
  localizedAgentHomepagePaths[language].replace(/\/:location\??/g, location ? `/${location}` : '');

function generateHomePageRoutes(): RouteRecordRaw[] {
  return Object.entries(localizedHomepagePaths).map(([locale, path]) => {
    return {
      path,
      name: getHomeRouteName(locale),
      component: AgencyDirectoryRouteComponent,
      meta: {
        locale: locale as HgSupportedLanguage,
        viewName: `${VIEW_NAME_PREFIX} Home Page`,
        getLanguageSwitchLink: (
          route: RouteLocationNormalized,
          language: HgSupportedLanguage,
          selectedLocations?: GeoLocation[],
        ) => {
          let locationName = route.params.location;

          if (selectedLocations && selectedLocations.length > 0) {
            // We always add the first selected location in the path, which need to be translated and transformed.
            locationName = selectedLocations[0].urlNames[language];
          }

          return {
            name: getHomeRouteName(language),
            query: route.query,
            params: { ...route.params, location: locationName },
          };
        },
      },
    } as unknown as RouteRecordRaw;
  });
}

function generateAgentHomePageRoutes(): RouteRecordRaw[] {
  return Object.entries(localizedAgentHomepagePaths).map(([locale, path]) => {
    return {
      path,
      name: getAgentHomeRouteName(locale),
      component: AgentDirectoryPage,
      meta: {
        locale: locale as HgSupportedLanguage,
        viewName: `${AGENT_VIEW_NAME_PREFIX} Home Page`,
        getLanguageSwitchLink: (
          route: RouteLocationNormalized,
          language: HgSupportedLanguage,
          selectedLocations?: GeoLocation[],
        ) => {
          let locationName = route.params.location;

          if (selectedLocations && selectedLocations.length > 0) {
            locationName = selectedLocations[0].urlNames[language];
          }

          return {
            name: getAgentHomeRouteName(language),
            query: route.query,
            params: { ...route.params, location: locationName },
          };
        },
      },
    } as unknown as RouteRecordRaw;
  });
}
